import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  LoaderPinwheel,
  Search,
  UserRoundCheck,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import InputField from "../ui/InputField";
import PageSizeSelect from "../ui/PageSizeSelect";
import RolCombobox from "../ui/RolCombobox";
import SelectField from "../ui/SelectField";
import {
  asignarSegundoFirmante,
  estadoServicio,
  filtrarElegibles,
  listarElegibles,
  listarRolesFirmante,
  opcionesFiltro,
  puedeSerFirmante,
  rangoUltimoMes,
} from "../../utils/calibracionRevision";

const ESTADOS = [{ _id: "Pendiente" }, { _id: "Borrador" }, { _id: "Finalizada" }];
const ESTADO_BADGE = {
  Pendiente: "bg-amber-50 text-amber-700 border border-amber-200",
  Borrador: "bg-violet-50 text-violet-700 border border-violet-200",
  Finalizada: "bg-green-50 text-green-700 border border-green-200",
};

const FILTROS_INICIALES = {
  cliente: "",
  sedes: [],
  areas: [],
  metrologo: "",
  estado: "",
  busqueda: "",
};

const idsDe = (e) => e.target.value.map((o) => o._id);

// Asigna en bloque el segundo firmante de calibraciones pendientes, en borrador o finalizadas.
const AsignarSegundoFirmanteModal = ({ onClose, onAsignado }) => {
  const [rango, setRango] = useState(() => rangoUltimoMes());
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [firmante, setFirmante] = useState("");
  const [rol, setRol] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const cargarFilas = useCallback(async () => {
    if (!rango.desde || !rango.hasta) return;
    setCargando(true);
    try {
      setFilas(await listarElegibles(rango));
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al cargar las calibraciones");
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, [rango]);

  useEffect(() => {
    cargarFilas();
    setSeleccion(new Set());
    setPage(1);
  }, [cargarFilas]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    axios
      .get(`${process.env.REACT_APP_API_URL}/users/resp`, { headers })
      .then((r) => setUsuarios((r.data || []).filter(puedeSerFirmante)))
      .catch(() => toast.error("Error al cargar los usuarios"));
    listarRolesFirmante()
      .then(setRoles)
      .catch(() => toast.error("Error al cargar los roles"));
  }, []);

  const opciones = useMemo(() => opcionesFiltro(filas, filtros), [filas, filtros]);
  const visibles = useMemo(() => filtrarElegibles(filas, filtros), [filas, filtros]);
  const totalPaginas = Math.max(1, Math.ceil(visibles.length / pageSize));
  const pagina = useMemo(() => {
    const inicio = (page - 1) * pageSize;
    return visibles.slice(inicio, inicio + pageSize);
  }, [visibles, page, pageSize]);

  // "Seleccionar todas" cubre todos los resultados del filtro, no solo la página
  const todasMarcadas =
    visibles.length > 0 && visibles.every((s) => seleccion.has(s._id));

  const actualizarFiltro = (cambios) => {
    setFiltros((prev) => ({ ...prev, ...cambios }));
    setPage(1);
  };

  const alternar = (id) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const alternarTodas = () =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      visibles.forEach((s) => (todasMarcadas ? next.delete(s._id) : next.add(s._id)));
      return next;
    });

  // El metrólogo de una calibración seleccionada no puede ser su revisor
  const firmantesDisponibles = useMemo(() => {
    const metrologos = new Set();
    filas.forEach((s) => {
      if (!seleccion.has(s._id)) return;
      [s.metrologo?._id, s.personaAsignada?._id].forEach(
        (id) => id && metrologos.add(id),
      );
    });
    return usuarios.filter((u) => !metrologos.has(u._id));
  }, [filas, seleccion, usuarios]);

  useEffect(() => {
    if (firmante && !firmantesDisponibles.some((u) => u._id === firmante)) {
      setFirmante("");
    }
  }, [firmante, firmantesDisponibles]);

  const elegirFirmante = (e) => {
    const id = e.target.value;
    setFirmante(id);
    const elegido = usuarios.find((u) => u._id === id);
    if (elegido?.rolSegundoFirmante) setRol(elegido.rolSegundoFirmante);
  };

  const asignar = async () => {
    setGuardando(true);
    try {
      const { message, resultados } = await asignarSegundoFirmante({
        cronogramaIds: [...seleccion],
        usuario: firmante,
        rol,
      });
      const fallidas = resultados.filter((r) => !r.ok);
      toast.success(message);
      if (fallidas.length) {
        toast.warning(
          `${fallidas.length} no se pudo(pudieron) asignar: ${fallidas[0].motivo}`,
        );
      }
      setSeleccion(new Set(fallidas.map((r) => r.id)));
      setFirmante("");
      setRol("");
      onAsignado?.();
      listarRolesFirmante().then(setRoles).catch(() => {});
      await cargarFilas();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al asignar el segundo firmante");
    } finally {
      setGuardando(false);
    }
  };

  const puedeAsignar = seleccion.size > 0 && firmante && rol.trim() && !guardando;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 backdrop-blur-sm sm:p-4">
      <div className="flex max-h-[95vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-blue-600 p-2">
              <UserRoundCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Segundo firmante de calibraciones</h2>
              <p className="text-xs text-gray-500">
                Selecciona las calibraciones y designa quién las revisará y firmará.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          {/* Filtros */}
          <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <InputField
              label="Desde"
              type="date"
              value={rango.desde}
              max={rango.hasta}
              onChange={(e) => setRango((r) => ({ ...r, desde: e.target.value }))}
            />
            <InputField
              label="Hasta"
              type="date"
              value={rango.hasta}
              min={rango.desde}
              onChange={(e) => setRango((r) => ({ ...r, hasta: e.target.value }))}
            />
            <SelectField
              label="Cliente"
              name="cliente"
              value={filtros.cliente}
              options={opciones.clientes}
              placeholder="Todos los clientes"
              onChange={(e) =>
                actualizarFiltro({ cliente: e.target.value, sedes: [], areas: [] })
              }
            />
            <SelectField
              label="Sedes"
              name="sedes"
              isMulti
              value={filtros.sedes}
              options={opciones.sedes}
              placeholder="Todas las sedes"
              onChange={(e) => actualizarFiltro({ sedes: idsDe(e), areas: [] })}
            />
            <SelectField
              label="Áreas"
              name="areas"
              isMulti
              value={filtros.areas}
              options={opciones.areas}
              placeholder="Todas las áreas"
              onChange={(e) => actualizarFiltro({ areas: idsDe(e) })}
            />
            <SelectField
              label="Metrólogo"
              name="metrologo"
              value={filtros.metrologo}
              options={opciones.metrologos}
              placeholder="Todos los metrólogos"
              onChange={(e) => actualizarFiltro({ metrologo: e.target.value })}
            />
            <SelectField
              label="Estado"
              name="estado"
              value={filtros.estado}
              options={ESTADOS}
              displayFn={(o) => o._id}
              placeholder="Todos los estados"
              onChange={(e) => actualizarFiltro({ estado: e.target.value })}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Buscar</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={filtros.busqueda}
                  onChange={(e) => actualizarFiltro({ busqueda: e.target.value })}
                  placeholder="Serial, código, marca…"
                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2">
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600">
                <input
                  type="checkbox"
                  className="cursor-pointer rounded border-gray-300"
                  checked={todasMarcadas}
                  onChange={alternarTodas}
                  disabled={!visibles.length}
                />
                Seleccionar todas ({visibles.length})
              </label>
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-xs uppercase tracking-wide text-gray-600">
                    <th className="w-10 px-4 py-3" aria-label="Selección" />
                    <th className="px-4 py-3 text-left">Equipo</th>
                    <th className="px-4 py-3 text-left">Cliente / Sede</th>
                    <th className="px-4 py-3 text-left">Área</th>
                    <th className="px-4 py-3 text-left">Metrólogo</th>
                    <th className="px-4 py-3 text-left">Fecha</th>
                    <th className="px-4 py-3 text-left">Estado</th>
                    <th className="px-4 py-3 text-left">Firmante actual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cargando ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-sm text-gray-400">
                        <LoaderPinwheel className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-400" />
                        Cargando calibraciones...
                      </td>
                    </tr>
                  ) : pagina.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center">
                        <UserRoundCheck className="mx-auto mb-2 h-10 w-10 text-gray-200" />
                        <p className="font-medium text-gray-500">No se encontraron calibraciones</p>
                        <p className="mt-1 text-xs text-gray-400">
                          Amplía el rango de fechas o ajusta los filtros
                        </p>
                      </td>
                    </tr>
                  ) : (
                    pagina.map((s) => {
                      const estado = estadoServicio(s);
                      return (
                        <tr key={s._id} className="transition-colors hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-center">
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar ${s.equipo?.serial || s._id}`}
                              className="cursor-pointer rounded border-gray-300"
                              checked={seleccion.has(s._id)}
                              onChange={() => alternar(s._id)}
                            />
                          </td>
                          <td className="px-4 py-2.5">
                            <p className="text-xs font-medium text-gray-800">
                              {s.equipo?.marca?.name || "—"} {s.equipo?.modelo?.name || ""}
                            </p>
                            <p className="mt-0.5 font-mono text-xs text-gray-400">
                              {s.equipo?.serial || "—"}
                            </p>
                          </td>
                          <td className="px-4 py-2.5 text-xs">
                            <p className="font-medium text-gray-700">
                              {s.equipo?.cliente?.name || "—"}
                            </p>
                            <p className="text-gray-400">{s.equipo?.sede?.name || "—"}</p>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">
                            {s.equipo?.area?.name || "—"}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">
                            {s.metrologo?.name || (
                              <span className="italic text-gray-400">Sin asignar</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-600">
                            {new Date(s.fechaEfectiva).toLocaleDateString("es-CO")}
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${ESTADO_BADGE[estado]}`}
                            >
                              {estado}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">
                            {s.segundoFirmante?.usuario?.name ? (
                              <>
                                <p className="font-medium text-gray-700">
                                  {s.segundoFirmante.usuario.name}
                                </p>
                                <p className="text-gray-400">{s.segundoFirmante.rol}</p>
                              </>
                            ) : (
                              <span className="italic text-gray-400">Sin asignar</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2">
              <span className="text-xs text-gray-400">
                {seleccion.size} seleccionada(s)
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Página anterior"
                  className="rounded-lg border border-gray-300 p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium">
                  {page} / {totalPaginas}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                  disabled={page === totalPaginas}
                  aria-label="Página siguiente"
                  className="rounded-lg border border-gray-300 p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Asignación */}
          <div className="grid grid-cols-1 gap-3 rounded-xl border border-blue-100 bg-blue-50/40 p-4 sm:grid-cols-2">
            <SelectField
              label="Segundo firmante"
              name="firmante"
              value={firmante}
              options={firmantesDisponibles}
              placeholder="Selecciona un usuario"
              onChange={elegirFirmante}
            />
            <RolCombobox
              label="Rol del firmante"
              value={rol}
              onChange={setRol}
              roles={roles}
              disabled={guardando}
            />
            <p className="text-xs text-gray-500 sm:col-span-2">
              Solo aparecen usuarios con acceso a Mis servicios. Las calibraciones
              pendientes o en borrador llegan al firmante cuando el metrólogo las
              finaliza, y su certificado público queda en revisión hasta la firma.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <Button type="button" variant="outline" onClick={onClose} disabled={guardando}>
            Cerrar
          </Button>
          <Button type="button" onClick={asignar} disabled={!puedeAsignar}>
            <UserRoundCheck size={14} className="mr-1.5" />
            {guardando ? "Asignando..." : `Asignar a ${seleccion.size} calibración(es)`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AsignarSegundoFirmanteModal;
